import { FormEvent, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  createDraftClauseAlternative,
  updateDraftClauseAlternative,
} from "@/lib/api/drafts";
import { pageDescriptionMobileHiddenClassName } from "@/lib/page-action-button";
import type { ClausePosture, DraftTemplateSectionOption } from "@/types/drafting";

const POSTURE_OPTIONS: Array<{ value: ClausePosture; label: string }> = [
  { value: "pro-company", label: "Pro-Company (Strict)" },
  { value: "balanced", label: "Balanced" },
  { value: "pro-counterparty", label: "Pro-Executive (Lenient)" },
  { value: "market-standard", label: "Standard" },
];

export interface DraftClauseSheetDraft {
  mode: "create" | "edit" | "customize";
  id?: string;
  template_id: string;
  section_key: string;
  clause_category: string;
  language: string;
  posture: ClausePosture | "";
  jurisdiction_applicability: string;
  approval_status: string;
  version: string;
}

interface DraftClauseSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  draft: DraftClauseSheetDraft | null;
  sections: DraftTemplateSectionOption[];
  onSaved?: () => void;
}

export function DraftClauseSheet({
  open,
  onOpenChange,
  draft,
  sections,
  onSaved,
}: DraftClauseSheetProps) {
  const [sectionKey, setSectionKey] = useState("");
  const [clauseCategory, setClauseCategory] = useState("");
  const [language, setLanguage] = useState("");
  const [posture, setPosture] = useState<ClausePosture | "">("");
  const [jurisdictionApplicability, setJurisdictionApplicability] = useState("general");
  const [approvalStatus, setApprovalStatus] = useState("approved");
  const [version, setVersion] = useState("v1.0");
  const [isSaving, setIsSaving] = useState(false);

  const title = useMemo(() => {
    if (!draft) return "Clause variant";
    if (draft.mode === "edit") return "Edit clause variant";
    if (draft.mode === "customize") return "Customize clause variant";
    return "New clause variant";
  }, [draft]);

  const lockVariantIdentity = draft?.mode === "customize";

  useEffect(() => {
    if (!open || !draft) return;
    setSectionKey(draft.section_key);
    setClauseCategory(draft.clause_category);
    setLanguage(draft.language);
    setPosture(draft.posture);
    setJurisdictionApplicability(draft.jurisdiction_applicability);
    setApprovalStatus(draft.approval_status);
    setVersion(draft.version);
  }, [open, draft]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!draft || !sectionKey || !language.trim()) return;

    setIsSaving(true);
    try {
      const payload = {
        section_key: sectionKey,
        clause_category: clauseCategory.trim() || undefined,
        language: language.trim(),
        posture: posture || undefined,
        jurisdiction_applicability: jurisdictionApplicability.trim() || "general",
        approval_status: approvalStatus,
        version: version.trim() || "v1.0",
      };

      if ((draft.mode === "edit" || draft.mode === "customize") && draft.id) {
        await updateDraftClauseAlternative(draft.id, payload);
      } else {
        await createDraftClauseAlternative({
          template_id: draft.template_id,
          ...payload,
        });
      }

      onSaved?.();
      onOpenChange(false);
    } catch (reason) {
      console.error(reason);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription className={pageDescriptionMobileHiddenClassName}>
            {draft?.mode === "customize"
              ? "Save your organization's version of this system variant. Other organizations keep the default."
              : "Add firm-approved wording for a specific document section. Drafters can swap variants in the Clauses panel."}
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <SheetBody className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="clause-section">Section</Label>
              <select
                id="clause-section"
                value={sectionKey}
                onChange={(event) => setSectionKey(event.target.value)}
                disabled={lockVariantIdentity}
                className="h-10 w-full rounded-md border border-[var(--lm-border-subtle)] bg-white px-3 text-sm disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900/50"
                required
              >
                <option value="">Select section…</option>
                {sections.map((section) => (
                  <option key={section.key} value={section.key}>
                    {section.title}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="clause-category">Clause category</Label>
              <Input
                id="clause-category"
                value={clauseCategory}
                onChange={(event) => setClauseCategory(event.target.value)}
                disabled={lockVariantIdentity}
                placeholder="e.g. base-salary, non-compete"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="clause-posture">Posture</Label>
              <select
                id="clause-posture"
                value={posture}
                onChange={(event) => setPosture(event.target.value as ClausePosture | "")}
                disabled={lockVariantIdentity}
                className="h-10 w-full rounded-md border border-[var(--lm-border-subtle)] bg-white px-3 text-sm disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900/50"
              >
                <option value="">Not specified</option>
                {POSTURE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="clause-language">Clause language</Label>
              <Textarea
                id="clause-language"
                value={language}
                onChange={(event) => setLanguage(event.target.value)}
                rows={6}
                required
                placeholder="Enter the approved clause text…"
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="clause-jurisdiction">Jurisdiction applicability</Label>
                <Input
                  id="clause-jurisdiction"
                  value={jurisdictionApplicability}
                  onChange={(event) => setJurisdictionApplicability(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="clause-version">Version</Label>
                <Input
                  id="clause-version"
                  value={version}
                  onChange={(event) => setVersion(event.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="clause-approval">Approval status</Label>
              <select
                id="clause-approval"
                value={approvalStatus}
                onChange={(event) => setApprovalStatus(event.target.value)}
                className="h-10 w-full rounded-md border border-[var(--lm-border-subtle)] bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-900/50"
              >
                <option value="approved">Approved</option>
                <option value="unverified">Unverified</option>
              </select>
            </div>
          </SheetBody>

          <SheetFooter>
            <Button type="button" variant="surface" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving || !language.trim() || !sectionKey}>
              {isSaving
                ? "Saving…"
                : draft?.mode === "customize"
                  ? "Save for my organization"
                  : draft?.mode === "edit"
                    ? "Save changes"
                    : "Add variant"}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
